import { Logger } from '@/Logger';

import { TeoWritableStore } from '../../state/TeoStore';
import type { AiQuickSuggestionsData, SuggestionPreset } from '../../TeoApi';
import { TeoEventTarget } from '../../utility/TeoEventTarget';
import type { AnalysisResult } from '../AnalysisResult';
import type { TeoText } from '../TeoText';
import type { AIHandler } from './AIHandler';
import { type MarkerType } from './aiSuggestionPresets';
import {
  prepareQuickRequest,
  quickSuggestionPreset,
  sendQuickRequest,
  type RequestedParagraph,
} from './quickSuggestionRequest';

const LOG = Logger('QuickSuggestionHandler');

/** The only marker the quick flow works on, since it rewrites whole paragraphs. */
const PARAGRAPH_MARKER = 'paragraph' satisfies MarkerType;

export type QuickSuggestionStatus = 'pending' | 'applied' | 'dismissed' | 'stale';

/** A text the AI suggests, with the improvement it brings to the TPI of the whole text. */
export type SuggestedText = {
  text: string;
  tpi: number;
};

export type QuickSuggestion = {
  markerId: string;
  paragraphIndex: number;
  /** The paragraph content at the time of the request, used to detect edits before applying. */
  originalText: string;
  replacement: string;
  /** Difference to the TPI of the unchanged text. */
  tpi: number;
  hidden?: boolean;
  status: QuickSuggestionStatus;
  /** Further suggestions for the same paragraph, best first. */
  alternatives: SuggestedText[];
};

export type QuickSuggestionHandlerEvents = {
  suggestionsReceived: (detail: QuickSuggestion[]) => void;
  suggestionsInvalidated: (detail?: void) => void;
};

/** Everything the UI shows of the quick suggestions. */
export class QuickSuggestionState {
  /** The suggestions of the last request, in document order. */
  suggestions: QuickSuggestion[] = [];
  isLoading = false;
  /** True once a request has finished, even if the AI suggested nothing. */
  requested = false;

  /** The suggestions that are neither applied nor dismissed yet. */
  get pendingSuggestions(): QuickSuggestion[] {
    return this.suggestions.filter((suggestion) => suggestion.status === 'pending');
  }
}

/**
 * Suggestions without the user having to select a paragraph first.
 *
 * All paragraphs are sent at once so that the AI can pick the ones it wants to change
 * itself, which is why the response usually covers only some of them. The paragraphs are
 * identified by the analysis, but their content is read from the editor, so a request
 * always reflects what the user currently sees.
 */
export class QuickSuggestionHandler {
  public events = new TeoEventTarget<QuickSuggestionHandlerEvents>();
  /** The state the UI reads and subscribes to. */
  public readonly store = new TeoWritableStore(new QuickSuggestionState());
  /** The analysis the current suggestions belong to. Their marker ids are only valid for it. */
  private requestedFor: AnalysisResult | null = null;
  private diffShown = false;
  /** The paragraph that is highlighted in the editor while the user looks at its suggestion. */
  private focusedMarkerId: string | null = null;
  private paragraphMarkersShown = false;

  constructor(
    private text: TeoText,
    private ai: AIHandler,
  ) {
    this.text.events.addEventListener('gotAnalysis', () => this.invalidate());
  }

  /** All currently available suggestions, in document order. */
  get suggestions(): QuickSuggestion[] {
    return this.store.state.suggestions;
  }

  get isLoading(): boolean {
    return this.store.state.isLoading;
  }

  /** False once the analysis the suggestions were requested for has been replaced. */
  get isValid(): boolean {
    return this.requestedFor !== null && this.text.analysis === this.requestedFor;
  }

  /**
   * The preset the current instruction asks for, or null while no instruction is active.
   */
  get preset(): SuggestionPreset | null {
    return quickSuggestionPreset(this.text);
  }

  /**
   * Sends all paragraphs of the text with the preset of the current instruction and lets
   * the AI choose which ones to improve. Replaces any suggestions from a previous request.
   * @returns The received suggestions, which may cover only some of the paragraphs.
   */
  async request(): Promise<QuickSuggestion[]> {
    const request = prepareQuickRequest(this.text);
    if (!request) return [];

    this.clear();
    this.requestedFor = request.analysis;
    this.store.setStateValue('isLoading', true);
    try {
      const response = await sendQuickRequest(this.text, this.ai, request);
      const suggestions = this.createSuggestions(response, request.paragraphs);
      this.store.setStateValue('suggestions', suggestions);
      this.store.setStateValue('requested', true);
      this.events.triggerEvent('suggestionsReceived', suggestions);
      return suggestions;
    } catch (error) {
      LOG.error('Failed to get quick suggestions:', error);
      this.text.core.notifications.addError(error);
      this.clear();
      throw error;
    } finally {
      this.store.setStateValue('isLoading', false);
    }
  }

  /**
   * Applies a suggestion to the editor, unless the paragraph changed since the request.
   * @param markerId The paragraph to apply the suggestion to.
   * @returns True if the replacement was made.
   */
  apply(markerId: string): boolean {
    const suggestion = this.suggestions.find((entry) => entry.markerId === markerId);
    if (!suggestion || suggestion.status !== 'pending') return false;
    if (!this.isValid) {
      this.invalidate();
      return false;
    }
    const currentText = this.text.markers.getMarkerContents([markerId])[0]?.text;
    if (currentText === undefined || currentText !== suggestion.originalText) {
      LOG.warn(`Paragraph ${markerId} changed since the suggestion was requested.`);
    }
    this.showDiff();
    this.text.markers.replaceMarkerContent(markerId, suggestion.replacement);
    this.setStatus(markerId, 'applied');
    return true;
  }

  /**
   * Applies every suggestion that is still pending and does not worsen the TPI.
   * @returns The number of applied suggestions.
   */
  applyAll(): number {
    return this.suggestions
      .filter((suggestion) => suggestion.status === 'pending' && !suggestion.hidden)
      .reduce((applied, suggestion) => applied + (this.apply(suggestion.markerId) ? 1 : 0), 0);
  }

  dismiss(markerId: string): void {
    const suggestion = this.suggestions.find((entry) => entry.markerId === markerId);
    if (!suggestion || suggestion.status !== 'pending') return;
    this.setStatus(markerId, 'dismissed');
  }

  /**
   * Highlights the paragraph a suggestion would replace, the way clicking a marker does in
   * the selection flow.
   * @param markerId The paragraph to highlight, or null to remove the highlight.
   */
  focus(markerId: string | null): void {
    if (this.focusedMarkerId === markerId) return;
    if (this.focusedMarkerId) {
      this.text.markers.removeFocusedMarker(this.focusedMarkerId);
    }
    this.focusedMarkerId = markerId;
    if (markerId === null) {
      this.hideParagraphMarkers();
      return;
    }
    this.showParagraphMarkers();
    this.text.markers.addFocusedMarker(markerId);
  }

  clear(): void {
    this.focus(null);
    this.store.setStateValue('suggestions', []);
    this.store.setStateValue('requested', false);
    this.requestedFor = null;
    this.diffShown = false;
  }

  /**
   * Drops the suggestions, because their marker ids no longer refer to the paragraphs
   * they were built from.
   */
  private invalidate(): void {
    if (this.suggestions.length === 0 && this.requestedFor === null) return;
    this.clear();
    this.events.triggerEvent('suggestionsInvalidated', void 0);
  }

  /** Replaces a suggestion, so that reading the state sees the new status. */
  private setStatus(markerId: string, status: QuickSuggestionStatus): void {
    this.store.setStateValue(
      'suggestions',
      this.suggestions.map((suggestion) =>
        suggestion.markerId === markerId ? { ...suggestion, status } : suggestion,
      ),
    );
  }

  private createSuggestions(
    data: AiQuickSuggestionsData,
    paragraphs: RequestedParagraph[],
  ): QuickSuggestion[] {
    const byId = new Map(paragraphs.map((paragraph) => [paragraph.id, paragraph]));
    const suggestions: QuickSuggestion[] = [];

    this.groupByParagraph(data).forEach((texts, id) => {
      const paragraph = byId.get(id);
      if (!paragraph) {
        LOG.warn('Received a suggestion for an unknown paragraph:', id);
        return;
      }
      // The response does not come in a reliable order, so the best one is picked here.
      const [best, ...alternatives] = texts.sort((a, b) => b.tpi - a.tpi);
      if (!best) return;

      suggestions.push({
        markerId: paragraph.markerId,
        paragraphIndex: paragraph.index,
        originalText: paragraph.text,
        replacement: best.text,
        tpi: best.tpi,
        hidden: best.tpi < 0,
        status: 'pending',
        alternatives,
      });
    });

    return suggestions.sort((a, b) => a.paragraphIndex - b.paragraphIndex);
  }

  /**
   * The suggested texts of a response, by the paragraph id they were made for, each with
   * the improvement it brings to the TPI of the whole text. Without the analysis of a
   * suggestion there is no improvement to show, which makes all of them equal.
   */
  private groupByParagraph(data: AiQuickSuggestionsData): Map<string, SuggestedText[]> {
    const baseTpi = data.base_result?.tpi.percentage;
    const byParagraph = new Map<string, SuggestedText[]>();
    data.suggestions.forEach((suggestion) => {
      const tpi = suggestion.result?.tpi.percentage;
      const texts = byParagraph.get(suggestion.id) ?? [];
      texts.push({
        text: suggestion.text,
        tpi: tpi !== undefined && baseTpi !== undefined ? tpi - baseTpi : 0,
      });
      byParagraph.set(suggestion.id, texts);
    });
    return byParagraph;
  }

  /**
   * A paragraph can only be highlighted while the editor draws its marker. Paragraph
   * markers are transparent until focused, so showing them changes nothing else.
   */
  private showParagraphMarkers(): void {
    // Another flow, usually the selection one, may already show them and will remove them again.
    if (this.text.markers.checkMarkerVisibility(PARAGRAPH_MARKER)) return;
    this.text.markers.addMarkers([PARAGRAPH_MARKER]);
    this.paragraphMarkersShown = true;
  }

  private hideParagraphMarkers(): void {
    if (!this.paragraphMarkersShown) return;
    this.paragraphMarkersShown = false;
    // The selection flow started using the markers in the meantime and still needs them.
    if (this.text.markers.clickableMarkers.includes(PARAGRAPH_MARKER)) return;
    this.text.markers.removeMarkers([PARAGRAPH_MARKER]);
  }

  /** Shows the applied changes in the editor, like the selection flow does. */
  private showDiff(): void {
    if (this.diffShown) return;
    this.text.diff.update(this.text.text, true);
    this.diffShown = true;
  }
}
